import React from 'react';
import styled from 'styled-components';
import { PaymentType } from '../../../stateMachines/submitCase/selectPaymentType';
import { User } from 'firebase/auth';
import { useSubmitCaseContext } from '../../../context/SubmitCaseContext';
import { useActor } from '@xstate/react';
import Typography from '../../common/Typography';
import creditCardIcon from '../../../images/submit-case/credit-card.svg';
import invoiceIcon from '../../../images/submit-case/invoice.svg';
import FullscreenLoader from '../../common/FullscreenLoader';
import AlertBox from '../../common/AlertBox';
import OptionList, { Option } from './OptionList';
import { IonButton } from '@ionic/react';

const ButtonContainer = styled.div`
  margin-top: 64px;
  padding: 0 8px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;
const InvoiceAlertContainer = styled.div`
  padding: 0 16px;
`;

// TODO use IonFooter and make it sticky?
const Container = styled.div`
  padding: 0 24px 32px 24px;
`;

interface SelectPaymentTypeProps {
  value: PaymentType;
  onChange: (value: PaymentType) => void;
  onNext: () => void;
  onBack: () => void;
  isLoading: boolean;
  hasPastDueInvoice: boolean;
  invoicingDisabled: boolean;
  title?: string;
}

export const SelectPaymentType: React.FC<SelectPaymentTypeProps> = ({
  value,
  onChange,
  onNext,
  onBack,
  isLoading,
  hasPastDueInvoice,
  invoicingDisabled,
  title = 'Payment Type',
}) => {
  return (
    <FullscreenLoader show={isLoading}>
      {!isLoading && (
        <Container>
          <Typography
            as="h3"
            fontSize="20px"
            variant="secondary"
            margin="16px 8px"
          >
            {title}
          </Typography>
          <Typography fontSize="12px" margin="16px 8px">
            Choose payment method for this case
          </Typography>

          <OptionList>
            <Option
              inputProps={{
                name: 'selectPaymentType',
                value: PaymentType.CREDIT_CARD,
                checked: value === PaymentType.CREDIT_CARD,
                onChange: (e) => onChange(PaymentType.CREDIT_CARD),
              }}
              onClick={() => onChange(PaymentType.CREDIT_CARD)}
              label="Credit card"
              details="Your card will be charged once the case is signed off."
              icon={<img src={creditCardIcon} alt="Credit card" />}
            />
            <Option
              inputProps={{
                name: 'selectPaymentType',
                value: PaymentType.INVOICE,
                checked: value === PaymentType.INVOICE,
                onChange: (e) => onChange(PaymentType.INVOICE),
                disabled: hasPastDueInvoice || invoicingDisabled,
              }}
              onClick={() => onChange(PaymentType.INVOICE)}
              label="Invoice"
              details="Invoices are sent at the beginning of the next month and are due within 14 days of receipt."
              icon={<img src={invoiceIcon} alt="Invoice" />}
            />
          </OptionList>

          {hasPastDueInvoice && (
            <InvoiceAlertContainer>
              <AlertBox
                type="warning"
                text={
                  <>
                    Invoicing has been disabled for your account because you
                    have a past due invoice. If you think this is in error,
                    please contact info@pocketpathologist.com.
                  </>
                }
              />
            </InvoiceAlertContainer>
          )}

          <ButtonContainer>
            <IonButton
              expand="block"
              fill="clear"
              type="button"
              onClick={onBack}
            >
              BACK
            </IonButton>

            <IonButton
              expand="block"
              type="submit"
              onClick={onNext}
              style={{ minWidth: '120px' }}
            >
              NEXT
            </IonButton>
          </ButtonContainer>
        </Container>
      )}
    </FullscreenLoader>
  );
};

const ControlledSelectPaymentType: React.FC<{ currentUser: User }> = (
  props
) => {
  const { submitCaseService } = useSubmitCaseContext();
  const [state, send] = useActor(submitCaseService);
  const isLoading = state.matches({ selectPaymentType: 'load' });
  return (
    <SelectPaymentType
      {...props}
      value={state.context.paymentType}
      onChange={(value) => {
        send({ type: 'SET_PAYMENT_TYPE', paymentType: value });
      }}
      onNext={() => send({ type: 'NEXT' })}
      onBack={() => send({ type: 'BACK' })}
      isLoading={isLoading}
      hasPastDueInvoice={state.context.hasPastDueInvoice}
      invoicingDisabled={state.context.invoicingDisabled}
    />
  );
};

export default ControlledSelectPaymentType;
